import { Popconfirm } from "antd";
import Search, { SearchProps } from "antd/es/input/Search";
import { FC, useCallback, useMemo } from "react";
import { ProductOrderItem } from "tm-shared/types";

import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { useScrollToTop } from "@/hooks/useScrollToTop";
import { setRejectProductsQuery } from "@/store/slices/ui";

import {
    SearchWrapper,
    StyledTotalSum,
    StyledTotalSumTitle,
    StyledUpdateInventoryBtn,
    StyledUpdateRemainingWarning,
    StyledUpdateRemainingWarningText,
} from "./RejectProductsTopSection.styled";

interface RejectProductsTopSectionProps {
    rejectProductsList: {
        [barcode: string]: ProductOrderItem;
    };
    taskId: string;

    handleConfirmUpdateRemaining: () => void;
}

export const RejectProductsTopSection: FC<RejectProductsTopSectionProps> = ({
    rejectProductsList,
    handleConfirmUpdateRemaining,
    taskId,
}) => {
    const dispatch = useAppDispatch();

    const { queryMap } = useAppSelector((state) => state.ui.rejectProducts);

    const hasRejectProducts = useMemo(
        () =>
            !!rejectProductsList && Object.keys(rejectProductsList).length > 0,
        [rejectProductsList],
    );

    const sum = useMemo(() => {
        return Object.values(rejectProductsList).reduce(
            (acc, product) => acc + (product.price || 0) * (product.count || 0),
            0,
        );
    }, [rejectProductsList]);

    const scrollToTopList = useScrollToTop(".reject-product-list");

    const onSearch: SearchProps["onSearch"] = useCallback(
        (query: string) => {
            dispatch(setRejectProductsQuery({ taskId, query }));
            scrollToTopList();
        },
        [dispatch, taskId, scrollToTopList],
    );

    return (
        <>
            <StyledTotalSum>
                <StyledTotalSumTitle>
                    Орієнтовна сума повернення: {sum.toFixed(2)} грн.
                </StyledTotalSumTitle>
                <p>Точна сума відповідатиме цінам діючим на дату поставки</p>
            </StyledTotalSum>

            <StyledUpdateRemainingWarning>
                <StyledUpdateRemainingWarningText>
                    Доступна для повернення кількість товарів обумовлена
                    залишками в магазині
                </StyledUpdateRemainingWarningText>

                {hasRejectProducts ? (
                    <Popconfirm
                        title="Продовжити?"
                        description="Перехід до завдання актуалізації залишків скасує всі Ваші поточні зміни у формі повернення товарів"
                        onConfirm={handleConfirmUpdateRemaining}
                        okText="Так"
                        cancelText="Ні"
                    >
                        <StyledUpdateInventoryBtn type="primary">
                            Актуалізувати залишки
                        </StyledUpdateInventoryBtn>
                    </Popconfirm>
                ) : (
                    <StyledUpdateInventoryBtn
                        type="primary"
                        onClick={handleConfirmUpdateRemaining}
                    >
                        Актуалізувати залишки
                    </StyledUpdateInventoryBtn>
                )}
            </StyledUpdateRemainingWarning>

            <SearchWrapper>
                <Search
                    placeholder="Пошук"
                    defaultValue={queryMap?.[taskId] ?? ""}
                    onSearch={onSearch}
                    allowClear
                    size="large"
                />
            </SearchWrapper>
        </>
    );
};
